/* @jsx h */
import { defineComponent, h, computed } from "vue";
import type { Reservation, ReservationRequest } from "@lemmonhq/types";
import { useLocalization } from "@lemmonhq/uikit";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import {
  isConfirmedStatus,
  categorizeReservation,
  TONE_CLASSES,
} from "../../../../../utils/reservationStatus";

dayjs.extend(utc);
dayjs.extend(timezone);

// "18:00" -> minutes since midnight (1080). Used to bucket bookings into service periods.
const hhmmToMinutes = (hhmm: string) => {
  const [h, m] = (hhmm || "").split(":").map(Number);
  return (h || 0) * 60 + (m || 0);
};

export type ServicePeriod = {
  name: string;
  start_time: string;
  end_time: string;
};

export type WeeklySummaryProps = {
  weekStart: Date;
  reservationsByDay: Record<string, Reservation[]>;
  pendingByDay?: Record<string, ReservationRequest[]>;
  totalTables: number;
  // Resolve the service periods for a specific calendar date (that day's
  // weekday shifts) — so Fri/Sat shifts bucket correctly per day.
  periodsForDate: (date: Date) => ServicePeriod[];
  timezone: string;
  onDayClick: (date: Date) => void;
  loading: boolean;
  // Metric toggle is owned by the page header now and passed down.
  metric: "reservations" | "people";
};

export const WeeklySummary = defineComponent<WeeklySummaryProps>({
  name: "WeeklySummary",
  props: [
    "weekStart",
    "reservationsByDay",
    "pendingByDay",
    "totalTables",
    "periodsForDate",
    "timezone",
    "onDayClick",
    "loading",
    "metric",
  ],
  setup(props) {
    const { t } = useLocalization();

    const days = computed(() => {
      const result = [];
      // "Today" in the RESTAURANT timezone — the week is grouped in that tz, so
      // a cross-tz manager gets the right today-highlight / past-day dimming.
      // YYYY-MM-DD strings compare correctly with the day keys.
      const todayKey = dayjs().tz(props.timezone).format("YYYY-MM-DD");
      for (let i = 0; i < 7; i++) {
        const date = new Date(props.weekStart);
        date.setDate(date.getDate() + i);
        const key = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
        const reservations = props.reservationsByDay[key] || [];
        const pendingRequests = props.pendingByDay?.[key] || [];

        const isToday = key === todayKey;
        const isPast = key < todayKey;

        // Break down reservations by status — track both the count and the
        // total number of people (party size) so the view can toggle between them.
        const sumPeople = (arr: any[]) =>
          arr.reduce((s, x) => s + (Number(x?.party_size) || 0), 0);

        // Confirmed bucket = booked-but-not-seated. Includes "changed" (an
        // edited-but-still-confirmed booking) so an edit doesn't vanish from the
        // week counts while still holding its table on the floor.
        const confirmedRes = reservations.filter((r) =>
          isConfirmedStatus(r.status),
        );
        // "Seated" bucket = guests who showed up — seated/arrived/partially-seated
        // plus completed. The week view spans past days, so these counts are kept
        // persistent (not gated to today): once a booking is seated or completed it
        // still contributes, instead of dropping out and zeroing past days.
        const inProgressRes = reservations.filter((r) => {
          const c = categorizeReservation(r.status);
          return c === "seated" || c === "completed";
        });

        const confirmed = confirmedRes.length;
        const inProgress = inProgressRes.length;
        const pending = pendingRequests.length;
        const total = confirmed + inProgress + pending;

        const confirmedPeople = sumPeople(confirmedRes);
        const inProgressPeople = sumPeople(inProgressRes);
        const pendingPeople = sumPeople(pendingRequests);
        const totalPeople = confirmedPeople + inProgressPeople + pendingPeople;

        // Per-shift (service period) breakdown — additive to the day-level total.
        // Each booking's UTC time is converted to the restaurant timezone, then matched
        // to the service period whose [start, end) range it falls into. Reservations use
        // `time`, pending requests use `requested_time`. Anything outside every period
        // lands in "Other", so the breakdown always reconciles with the day total.
        // We bucket exactly the same bookings that make up the displayed day total
        // (confirmed + seated/completed + pending, persistent across the week), so the
        // shift numbers reconcile with the toggle the user sees — counts in
        // "reservations" mode, party size in "people".
        const periods = props.periodsForDate?.(date) ?? [];
        const shifts: {
          name: string;
          isOther: boolean;
          count: number;
          people: number;
        }[] = [];
        if (periods.length > 0) {
          const byLabel = new Map<string, { count: number; people: number }>();
          let otherCount = 0;
          let otherPeople = 0;
          const labelFor = (p: {
            name: string;
            start_time: string;
            end_time: string;
          }) => p.name || `${p.start_time}–${p.end_time}`;
          const assign = (utcTime: string | undefined, partySize: number) => {
            if (!utcTime) return;
            const local = dayjs(utcTime).tz(props.timezone);
            const mins = local.hour() * 60 + local.minute();
            const period = periods.find((p) => {
              const start = hhmmToMinutes(p.start_time);
              let end = hhmmToMinutes(p.end_time);
              if (end <= start) end += 24 * 60; // crosses midnight (e.g. 18:00–02:00)
              // A booking in the small hours (before `start`) belongs to the
              // previous evening's wrapped period, so shift it forward a day too.
              const m = mins < start && end > 24 * 60 ? mins + 24 * 60 : mins;
              return m >= start && m < end;
            });
            if (period) {
              const label = labelFor(period);
              const cur = byLabel.get(label) ?? { count: 0, people: 0 };
              cur.count += 1;
              cur.people += partySize;
              byLabel.set(label, cur);
            } else {
              otherCount += 1;
              otherPeople += partySize;
            }
          };
          confirmedRes.forEach((r) =>
            assign(r.time, Number(r.party_size) || 0),
          );
          inProgressRes.forEach((r) =>
            assign(r.time, Number(r.party_size) || 0),
          );
          pendingRequests.forEach((r) =>
            assign(r.requested_time, Number(r.party_size) || 0),
          );

          const seen = new Set<string>();
          for (const p of periods) {
            const label = labelFor(p);
            if (seen.has(label)) continue;
            seen.add(label);
            const v = byLabel.get(label);
            if (v && (v.count > 0 || v.people > 0))
              shifts.push({
                name: label,
                isOther: false,
                count: v.count,
                people: v.people,
              });
          }
          if (otherCount > 0 || otherPeople > 0)
            shifts.push({
              name: "Other",
              isOther: true,
              count: otherCount,
              people: otherPeople,
            });
        }

        result.push({
          date,
          key,
          dayName: date.toLocaleDateString("en-US", { weekday: "short" }),
          dateLabel: date.toLocaleDateString(undefined, {
            month: "short",
            day: "numeric",
          }),
          isToday,
          isPast,
          confirmed,
          inProgress,
          pending,
          total,
          confirmedPeople,
          inProgressPeople,
          pendingPeople,
          totalPeople,
          shifts,
        });
      }
      return result;
    });

    return () => {
      if (props.loading) {
        return (
          <div>
            {/* Skeleton legend */}
            <div class="flex items-center gap-4 mb-4 px-1">
              {[1, 2, 3].map((i) => (
                <div key={i} class="flex items-center gap-1.5">
                  <div class="w-5 h-5 rounded-md bg-base-300/40 animate-pulse" />
                  <div class="w-14 h-3 rounded bg-base-300/30 animate-pulse" />
                </div>
              ))}
            </div>
            {/* Skeleton rows */}
            <div class="rounded-xl overflow-hidden border border-base-content/10">
              {[1, 2, 3, 4, 5, 6, 7].map((i) => (
                <div
                  key={i}
                  class={`flex items-center gap-4 px-5 ${i < 7 ? "border-b border-base-content/10" : ""}`}
                  style="padding-top: 12px; padding-bottom: 12px;"
                >
                  <div class="w-8 h-4 rounded bg-base-300/40 animate-pulse" />
                  <div class="w-14 h-3 rounded bg-base-300/30 animate-pulse" />
                  <div class="flex-1 h-6 rounded-md bg-base-content/5 animate-pulse" />
                  <div class="w-6 h-4 rounded bg-base-300/30 animate-pulse" />
                </div>
              ))}
            </div>
          </div>
        );
      }

      // Pick the active metric (reservation count vs total people) for every number.
      const isPeople = props.metric === "people";
      const cVal = (d: any) => (isPeople ? d.confirmedPeople : d.confirmed);
      const ipVal = (d: any) => (isPeople ? d.inProgressPeople : d.inProgress);
      const pVal = (d: any) => (isPeople ? d.pendingPeople : d.pending);
      const totVal = (d: any) => (isPeople ? d.totalPeople : d.total);
      // Scale = busiest day in the week (so the busiest day fills the full bar).
      const maxVal = Math.max(1, ...days.value.map((d) => totVal(d)));

      const totalConfirmed = days.value.reduce((sum, d) => sum + cVal(d), 0);
      const totalInProgress = days.value.reduce((sum, d) => sum + ipVal(d), 0);
      const totalPending = days.value.reduce((sum, d) => sum + pVal(d), 0);
      const grandTotal = totalConfirmed + totalInProgress + totalPending;

      return (
        <div>
          {/* Legend with count badges — one entry per status bucket */}
          <div class="flex items-center gap-4 mb-4 px-1">
            {[
              {
                legendBg: TONE_CLASSES.success.legendBg,
                count: totalConfirmed,
                label: "Confirmed",
              },
              {
                legendBg: TONE_CLASSES.error.legendBg,
                count: totalPending,
                label: "Pending",
              },
              {
                legendBg: TONE_CLASSES.info.legendBg,
                count: totalInProgress,
                label: "Seated",
              },
            ].map((entry) => (
              <div key={entry.label} class="flex items-center gap-1.5">
                <span
                  class={`text-xs font-semibold rounded-md text-accent text-center inline-flex items-center justify-center min-w-[20px] h-[20px] px-1 ${entry.legendBg}`}
                >
                  {entry.count}
                </span>
                <span class="text-xs text-base-content/60">{entry.label}</span>
              </div>
            ))}
            <div class="flex items-center gap-1.5 pl-3 ml-1 border-l border-base-content/10">
              <span class="text-xs font-semibold rounded-md text-base-100 text-center inline-flex items-center justify-center min-w-[20px] h-[20px] px-1 bg-base-content">
                {grandTotal}
              </span>
              <span class="text-xs font-medium text-base-content/80">
                Total
              </span>
            </div>
            {props.totalTables > 0 && (
              <span class="ml-auto hidden sm:inline text-xs text-base-content/30">
                Capacity: {props.totalTables} tables
              </span>
            )}
          </div>

          {/* Week rows */}
          <div class="rounded-xl overflow-hidden border border-base-content/10">
            {days.value.map((day, i) => (
              <div
                key={day.key}
                data-testid="week-summary-day-row"
                class={`flex items-center gap-4 px-5 cursor-pointer transition-colors hover:bg-base-200/30 ${
                  i < 6 ? "border-b border-base-content/10" : ""
                } ${day.isPast && !day.isToday ? "opacity-40" : ""}`}
                style={
                  day.isToday
                    ? "padding-top: 14px; padding-bottom: 14px; background: hsl(var(--su) / 0.06); border-left: 3px solid hsl(var(--su));"
                    : "padding-top: 12px; padding-bottom: 12px;"
                }
                onClick={() => props.onDayClick(day.date)}
              >
                {/* Day name */}
                <div class="w-10 flex-shrink-0">
                  <span
                    class={`text-sm font-bold ${day.isToday ? "text-success" : "text-base-content/70"}`}
                  >
                    {day.dayName}
                  </span>
                </div>

                {/* Date */}
                <div class="w-16 flex-shrink-0">
                  <span
                    class={`text-sm ${day.isToday ? "text-success font-semibold" : "text-base-content/40"}`}
                  >
                    {day.dateLabel}
                  </span>
                </div>

                {/* Stacked bar + per-shift breakdown */}
                <div class="flex-1 min-w-0">
                  {day.total > 0 ? (
                    <div class="h-6 bg-base-content/5 rounded-md overflow-hidden flex">
                      {/* One segment per bucket (seated, confirmed, pending).
                          Visibility gates on the DISPLAYED metric value (not the
                          raw count) so "people" mode never renders a 0-width
                          segment for a count-only day, and vice versa. */}
                      {[
                        {
                          barClass: TONE_CLASSES.info.barClass,
                          value: ipVal(day),
                        },
                        {
                          barClass: TONE_CLASSES.success.barClass,
                          value: cVal(day),
                        },
                        {
                          barClass: TONE_CLASSES.error.barClass,
                          value: pVal(day),
                        },
                      ]
                        .filter((seg) => seg.value > 0)
                        .map((seg, idx) => (
                          <div
                            key={idx}
                            class={`h-full ${seg.barClass} transition-all duration-500 flex items-center justify-center`}
                            style={{
                              width: `${(seg.value / maxVal) * 100}%`,
                              minWidth: "20px",
                            }}
                          >
                            <span class="text-[10px] font-bold text-accent/80">
                              {seg.value}
                            </span>
                          </div>
                        ))}
                    </div>
                  ) : (
                    <div class="h-6 flex items-center">
                      <span class="text-xs text-base-content/15">
                        {day.isPast ? "—" : "No reservations"}
                      </span>
                    </div>
                  )}
                  {(() => {
                    // Per-shift line follows the active metric so it reconciles with
                    // the day total the user sees (counts vs people).
                    const sVal = (s: any) => (isPeople ? s.people : s.count);
                    const visibleShifts = day.shifts.filter((s) => sVal(s) > 0);
                    if (visibleShifts.length === 0) return null;
                    return (
                      <div class="flex flex-wrap items-center gap-y-0.5 mt-1.5">
                        {visibleShifts.map((s, idx) => (
                          <span
                            key={s.name}
                            class="text-[10px] text-base-content/45 whitespace-nowrap"
                          >
                            {idx > 0 && (
                              <span class="text-base-content/20 mx-1">·</span>
                            )}
                            {s.isOther
                              ? t("reservations.schedule.other_shift")
                              : s.name}{" "}
                            <span class="font-semibold text-base-content/70">
                              {sVal(s)}
                            </span>
                          </span>
                        ))}
                      </div>
                    );
                  })()}
                </div>

                {/* Total count */}
                <div class="w-10 text-right flex-shrink-0">
                  <span
                    class={`text-sm ${day.total > 0 ? "font-bold text-base-content" : "text-base-content/15"}`}
                  >
                    {totVal(day) || "—"}
                  </span>
                </div>

                {/* Arrow */}
                <div class="w-4 flex-shrink-0">
                  <span class="material-symbols-outlined text-base text-base-content/20">
                    chevron_right
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      );
    };
  },
});
